import { ToggleButtonGroup } from "@mui/material";
import clsx from "clsx";
import { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import { ErrorsDisplayContainer } from "@ryde/controls";
import { useCrudEditContext } from "@ryde/redux";
import {
  archiveRelationErrorAtomFamily,
  CourseEntity,
  CourseTabsEnum,
  dynamicFormTabIndexAF,
  editFormDataAF,
  EditFormMode,
  errorsCrudContentAtomFamily,
  ICourseDto,
  isInnerItemEditingAtom,
  useIsMobile,
} from "@ryde/shared";

import { RelatedEntitiesErrorButton } from "../../../drawers/components/related-entities";
import { CourseEditTopInfoContainer } from "./course.edit.top-info";
import { CourseHeaderInfos } from "./course.header.infos";
import { CourseMap } from "./course.map";
import {
  CourseTabsEarsContainer,
  CourseTabsErrorContainer,
  CourseTabsWrapperStyled,
} from "./course.styled";
import { CourseTabsBottomBlocks } from "./course.tabs.bottom-blocks";
import { CourseTabsToggleButton } from "./course.tabs.styled";
import { CourseTabsTabAdvanced } from "./course.tabs.tab.advanced";
import { CourseTabsTabProperties } from "./course.tabs.tab.properties";
import { CourseTabsTabStations } from "./course.tabs.tab.stations";

export const CourseTabsWrapper: FC = () => {
  const {
    entity,
    mode,
    data: { isEdit },
  } = useCrudEditContext<ICourseDto>();
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const serverErrors = useRecoilValue(errorsCrudContentAtomFamily(entity));
  const hasArchiveRelationError = useRecoilValue(
    archiveRelationErrorAtomFamily(entity),
  );
  const courseDto = useRecoilValue(
    editFormDataAF([entity, mode || EditFormMode.CrudRoute]),
  );
  const [tab, setTab] = useRecoilState(dynamicFormTabIndexAF(entity));
  const isInnerItemEditing = useRecoilValue(isInnerItemEditingAtom);
  const {
    formState: { errors, isValid },
  } = useFormContext<Partial<CourseEntity>>();

  useEffect(() => {
    if (!isMobile && tab === CourseTabsEnum.Map) {
      setTab(CourseTabsEnum.Stations);
    }
  }, [isMobile, setTab, tab]);

  const handleTabSwitch = useEvent(
    (_: React.MouseEvent<HTMLElement>, nextView: CourseTabsEnum): void => {
      setTab(nextView);
    },
  );

  return (
    <CourseTabsWrapperStyled className="CourseDrawer-Form">
      <CourseEditTopInfoContainer />

      {isMobile && <CourseHeaderInfos />}

      <CourseTabsEarsContainer
        className={clsx(isInnerItemEditing && "inner-item-editing")}
      >
        <ToggleButtonGroup
          color="primary"
          size="small"
          value={tab}
          exclusive
          onChange={handleTabSwitch}
          aria-label="Platform"
        >
          <CourseTabsToggleButton
            value={CourseTabsEnum.Stations}
            disabled={tab === CourseTabsEnum.Stations}
          >
            {t("selectListLabel.stations")}
          </CourseTabsToggleButton>
          <CourseTabsToggleButton
            value={CourseTabsEnum.Properties}
            disabled={tab === CourseTabsEnum.Properties}
          >
            {t("selectListLabel.properties")}
          </CourseTabsToggleButton>
          <CourseTabsToggleButton
            value={CourseTabsEnum.Advanced}
            disabled={tab === CourseTabsEnum.Advanced}
          >
            {t("selectListLabel.advanced")}
          </CourseTabsToggleButton>

          {isMobile && (
            <CourseTabsToggleButton
              value={CourseTabsEnum.Map}
              disabled={tab === CourseTabsEnum.Map}
            >
              {t("selectListLabel.map")}
            </CourseTabsToggleButton>
          )}
        </ToggleButtonGroup>
        <CourseTabsErrorContainer>
          <ErrorsDisplayContainer
            formErrors={isValid ? undefined : errors}
            serverErrors={serverErrors}
          />
          {hasArchiveRelationError && isEdit && (
            <RelatedEntitiesErrorButton
              entity={entity}
              entityId={courseDto?.id}
              entityLabel={courseDto?.title || courseDto?.name}
            />
          )}
        </CourseTabsErrorContainer>
      </CourseTabsEarsContainer>

      {tab === CourseTabsEnum.Stations && <CourseTabsTabStations />}
      {tab === CourseTabsEnum.Properties && <CourseTabsTabProperties />}
      {tab === CourseTabsEnum.Advanced && <CourseTabsTabAdvanced />}
      {isMobile && tab === CourseTabsEnum.Map && <CourseMap mode={mode} />}
      <CourseTabsBottomBlocks />
    </CourseTabsWrapperStyled>
  );
};
